import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, ExternalLink, PenLine, Pencil, Plus, Trash2 } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";

export default function Forms() {
  const [forms, setForms] = useState(null);

  const load = () => api.get("/business/forms").then(({ data }) => setForms(data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
  }, []);

  const copyLink = (id) => {
    navigator.clipboard.writeText(`${window.location.origin}/f/${id}`);
    toast.success("Public form link copied");
  };

  const toggleActive = async (form) => {
    try {
      await api.patch(`/business/forms/${form.id}`, { ...form, active: !form.active });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this form? Submissions stay in conversations.")) return;
    try {
      await api.delete(`/business/forms/${id}`);
      toast.success("Form deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="forms-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-black">Forms</h1>
          <p className="text-sm text-neutral-500 mt-1">Build a form, share the link, receive submissions with uploads.</p>
        </div>
        <Link to="/dashboard/forms/new" data-testid="create-form-button">
          <Button className="rounded-full transition-transform duration-150 hover:-translate-y-0.5">
            <Plus className="h-4 w-4 mr-1.5" /> New form
          </Button>
        </Link>
      </div>

      <div className="mt-8 bg-white border border-[#E5E5E5] rounded-2xl divide-y divide-[#E5E5E5]">
        {!forms ? (
          <p className="p-6 text-sm text-neutral-400">Loading…</p>
        ) : forms.length === 0 ? (
          <p className="p-6 text-sm text-neutral-400" data-testid="forms-empty">No forms yet — create your first one.</p>
        ) : (
          forms.map((f) => (
            <div key={f.id} className="p-5 flex items-center gap-4 flex-wrap" data-testid={`form-row-${f.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-bold truncate">{f.title}</div>
                <div className="text-xs text-neutral-400 mt-0.5">
                  {f.fields.length} field(s) · {f.submission_count} submission(s)
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-neutral-500">
                Active
                <Switch checked={f.active} onCheckedChange={() => toggleActive(f)} data-testid={`form-active-${f.id}`} />
              </div>
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => copyLink(f.id)} data-testid={`form-copy-${f.id}`}>
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy link
              </Button>
              <a href={`/f/${f.id}`} target="_blank" rel="noreferrer" data-testid={`form-open-${f.id}`}>
                <Button variant="outline" size="sm" className="rounded-full">
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </a>
              <Link to={`/dashboard/forms/${f.id}/fill`} data-testid={`form-fill-${f.id}`}>
                <Button variant="outline" size="sm" className="rounded-full">
                  <PenLine className="h-3.5 w-3.5 mr-1" /> Fill
                </Button>
              </Link>
              <Link to={`/dashboard/forms/${f.id}/edit`} data-testid={`form-edit-${f.id}`}>
                <Button variant="outline" size="sm" className="rounded-full">
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                </Button>
              </Link>
              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(f.id)} data-testid={`form-delete-${f.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
